import { Component, useCallback, useEffect, useMemo, useState } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import { InteractiveRenderer } from '@aurora/interactives'
import { cardSession, pathStepHtml, rowStep, type InteractiveResult } from '@aurora/core'
import { auroraStore, useAuroraStore } from '../store/auroraStore'
import { activeChild, artifactsOfKind, childMemory } from '../store/selectors'
import { readSpec } from '../lib/specs'
import { gameFace } from '../lib/games'
import { useResolvedTheme } from '../hooks/useTheme'
import { useWakeLock } from '../hooks/useWakeLock'
import { play } from '../lib/feedback'
import { KidShell } from '../components/kid/KidShell'
import { Celebration } from '../components/kid/Celebration'
import { MaterialFrame } from '../components/kid/MaterialFrame'

/**
 * Kid Mode — the only surface the child touches.
 *
 * SAFETY, stated plainly: two things can run here and nothing else. A spec
 * that `readSpec` has validated reaches a hand-built renderer as the value
 * zod produced, never the stored object. A generated HTML material runs only
 * inside `MaterialFrame`: a sandboxed, separate-origin iframe under a CSP
 * with no network, whose only voice is a validated message. Anything else
 * falls through to the refusal below. The guard is the `if` chain, not a
 * convention.
 */
export function KidMode() {
  const artifact = useAuroraStore(
    (s: any) => (s.ui.selectedArtifactId ? s.artifacts[s.ui.selectedArtifactId] : undefined),
  )
  const blockRef = useAuroraStore((s: any) => s.ui.kidBlockRef as string | undefined)
  // Today's cards are composed at launch from every deck; they play here and log against the artifact.
  const launched = useAuroraStore((s: any) => s.ui.kidMaterial as { html: string; assets?: any[]; title?: string } | undefined)
  // A reload keeps the route but not the composed session: a deck opened
  // here with nothing composed is today's cards, made again the way the
  // cards page makes them at Start.
  const decks = useAuroraStore((s: any) => artifactsOfKind(s, 'deck')) as any[]
  const composed = useMemo(() => {
    if (launched || artifact?.kind !== 'deck') return launched
    const session = cardSession(decks)
    return session ? { html: session.html, assets: session.assets, title: "Today's cards" } : undefined
  }, [launched, artifact?.kind, decks])
  const child = useAuroraStore(activeChild)
  const memory = useAuroraStore(childMemory)
  const theme = useResolvedTheme()

  const [attempt, setAttempt] = useState(0)
  const [finished, setFinished] = useState<InteractiveResult | null>(null)
  const [progress, setProgress] = useState<{ step?: number; steps?: number }>({})
  const [crashed, setCrashed] = useState(false)

  useWakeLock(true)

  const read = useMemo(() => readSpec(artifact), [artifact])
  const fresh = useMemo(() => pathStepHtml(artifact), [artifact])
  const html: string | undefined = composed?.html ?? fresh ?? (typeof artifact?.html === 'string' && artifact.html.trim() ? artifact.html : undefined)

  const leave = useCallback(() => {
    // Back to wherever the parent opened it: the thread, the day, the Playroom.
    ;(auroraStore.getState() as any).exitKidMode()
  }, [])

  const onResult = useCallback(
    (result: InteractiveResult) => {
      const s = auroraStore.getState() as any
      if (child && artifact) {
        s.logInteractiveResult({
          childId: child._id,
          artifactId: artifact._id,
          result,
          blockRef,
        })
      }
      if (result.completed) setFinished(result)
      else leave()
    },
    [artifact, blockRef, child, leave],
  )

  const onPartial = useCallback(
    (result: InteractiveResult) => {
      if (!child || !artifact) return
      ;(auroraStore.getState() as any).logInteractiveResult({ childId: child._id, artifactId: artifact._id, result, blockRef })
    },
    [artifact, blockRef, child],
  )

  const onProgress = useCallback((raw: unknown) => {
    // The renderers own their own progress shape and more will be added, so
    // read the two fields we can use and ignore anything else.
    const p = raw as Record<string, unknown> | null
    if (!p || typeof p !== 'object') return
    const step = numberOr(p.index ?? p.step ?? p.current)
    const steps = numberOr(p.total ?? p.steps ?? p.count)
    setProgress((prev) =>
      prev.step === step && prev.steps === steps ? prev : { step, steps },
    )
  }, [])

  const again = useCallback(() => {
    play('tap')
    setFinished(null)
    setProgress({})
    setCrashed(false)
    setAttempt((n) => n + 1)
  }, [])

  return (
    <KidShell
      title={composed?.title ?? rowStep(artifact)?.title ?? artifact?.title}
      step={progress.step}
      steps={progress.steps}
      onExit={leave}
    >
      {!artifact ? (
        <Refusal
          title="Nothing to play yet"
          body="Pick something in the Playroom and it will open here."
          onExit={leave}
        />
      ) : crashed ? (
        <Refusal
          title="This one stopped working"
          body="Nothing is lost. Try it again, or ask Sapling to rebuild it."
          onRetry={again}
          onExit={leave}
        />
      ) : html ? (
        finished ? (
          <Celebration
            title={child ? `You did it, ${child.name}` : 'You did it'}
            detail={detailFor(finished)}
            onAgain={again}
            onDone={leave}
          />
        ) : (
          <KidCover key={attempt} artifact={artifact} title={composed?.title} childName={child?.name}>
          {(onReady) => (
          <MaterialFrame
            key={attempt}
            onReady={onReady}
            html={html}
            theme={theme}
            carry={artifact.carry}
            memory={memory}
            assets={composed ? composed.assets : artifact.assets}
            onResult={onResult}
            onPartial={onPartial}
            /* No onProgress: a generated material draws its own progress,
               so the shell's dots would be a second row saying the same. */
          />
          )}
          </KidCover>
        )
      ) : !read.ok ? (
        /* THE GUARD. No spec, no play — whatever else the artifact holds. */
        <Refusal
          title="This one is not ready to play"
          body="It did not come back in a shape Sapling can run safely, so it stays closed. Asking for it again usually sorts it out."
          detail={read.errors}
          onExit={leave}
        />
      ) : finished ? (
        <Celebration
          title={child ? `You did it, ${child.name}` : 'You did it'}
          detail={detailFor(finished)}
          onAgain={again}
          onDone={leave}
        />
      ) : (
        <RendererBoundary key={attempt} onCrash={() => setCrashed(true)}>
          <InteractiveRenderer
            spec={read.spec}
            theme={theme}
            onResult={onResult}
            onProgress={onProgress}
          />
        </RendererBoundary>
      )}
    </KidShell>
  )
}

/**
 * What she sees while a game opens. A game carries its voice and pictures
 * inside it, so the frame takes a few seconds to draw its first screen; until
 * it does, the game's own cover (its picture, and its name for the adult)
 * holds the stage, then fades into the game. Never a blank page.
 */
function KidCover({ artifact, title, childName, children }: { artifact: any; title?: string; childName?: string; children: (onReady: () => void) => ReactNode }) {
  const [ready, setReady] = useState(false)
  const [gone, setGone] = useState(false)
  // Once faded, the cover leaves the page so nothing sits over her game.
  useEffect(() => {
    if (!ready) return
    const id = window.setTimeout(() => setGone(true), 400)
    return () => window.clearTimeout(id)
  }, [ready])
  const onReady = useCallback(() => setReady(true), [])
  const { picture, emoji, letter } = gameFace(artifact, childName)
  return (
    <div className="kid-material">
      {children(onReady)}
      {!gone && (
        <div className={`kid-cover${ready ? ' is-ready' : ''}`} aria-hidden="true">
          <span className="kid-cover-face">
            {picture ? <img src={picture} alt="" /> : <span className="kid-cover-emoji">{emoji ?? letter}</span>}
          </span>
          <span className="kid-cover-title">{title ?? rowStep(artifact)?.title ?? artifact?.title}</span>
          <span className="kid-cover-dots"><i /><i /><i /></span>
        </div>
      )}
    </div>
  )
}

function numberOr(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

/**
 * What she gets told at the end.
 *
 * Effort and completion, never a score — she is two, and "7 out of 10" is a
 * verdict. The parent reads the real numbers later, in the Story.
 */
function detailFor(result: InteractiveResult): string {
  const total = result.score?.total ?? 0
  if (total > 0) return `You worked through all ${total} of them.`
  const minutes = Math.round(result.durationMs / 60_000)
  return minutes >= 1 ? `You stayed with it for ${minutes} minutes.` : 'All the way to the end.'
}

/**
 * A dead end here is a dead end for a two-year-old, so every one of them says
 * what happened and keeps the way out in reach of the adult next to her.
 */
function Refusal({
  title,
  body,
  detail,
  onRetry,
  onExit,
}: {
  title: string
  body: string
  detail?: string[]
  onRetry?: () => void
  onExit: () => void
}) {
  return (
    <div className="kid-refusal">
      <p className="kid-refusal-title">{title}</p>
      <p className="kid-refusal-body">{body}</p>
      {detail && detail.length > 0 && (
        <details className="kid-refusal-detail">
          <summary>What went wrong</summary>
          <ul>
            {detail.slice(0, 5).map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </details>
      )}
      <div className="kid-refusal-actions">
        {onRetry && (
          <button className="kid-btn" onClick={onRetry}>
            Try again
          </button>
        )}
        <button className="kid-btn kid-btn-quiet" onClick={onExit}>
          Leave Kid Mode
        </button>
      </div>
    </div>
  )
}

/**
 * A renderer that throws would leave a locked full-screen surface blank with a
 * child in front of it. This turns that into a message and a way out.
 */
class RendererBoundary extends Component<
  { children: ReactNode; onCrash: () => void },
  { failed: boolean }
> {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[kid mode] renderer failed', error, info.componentStack)
    this.props.onCrash()
  }

  render() {
    return this.state.failed ? null : this.props.children
  }
}
